import type { FC, PropsWithChildren } from "react";
import { createContext, useContext, useEffect, useMemo, useState } from "react";

import type { IEntryPointComponent } from "@/src/common/PageTransition";
import type { HeaderType } from "@/src/shared/IHeader";

export type IPageContext = {
    headerType: HeaderType;
    showFooter: boolean;
    pageTitle: string;
    setPageContext: (pageContext: IPageContextData) => void;
    canonicalUrl?: string;
    component: IEntryPointComponent;
};

type IPageContextData = Omit<IPageContext, "setPageContext">;

export const PageContext = createContext<IPageContext>(null);

export const usePageContext = () => useContext(PageContext);

export const PageContextProvider: FC<PropsWithChildren<IPageContextData>> = ({ children, ...props }) => {
    const [pageContext, setPageContext] = useState(props);

    const context = useMemo(
        () => ({
            ...pageContext,
            setPageContext: (context: IPageContext) => {
                if (
                    context.canonicalUrl !== pageContext.canonicalUrl ||
                    context.pageTitle !== pageContext.pageTitle ||
                    context.headerType !== pageContext.headerType ||
                    context.showFooter !== pageContext.showFooter
                ) {
                    setPageContext(context);
                }
            },
        }),
        [pageContext, setPageContext],
    );

    useEffect(() => {
        if (!pageContext.canonicalUrl) return;

        const canonicalLink = document.querySelector("link[rel='canonical']") as HTMLLinkElement;
        if (canonicalLink.href !== pageContext.canonicalUrl) {
            canonicalLink.href = pageContext.canonicalUrl;
        }
    }, [pageContext.canonicalUrl]);

    return <PageContext.Provider value={context}>{children}</PageContext.Provider>;
};
